/* @flow */
import * as React from 'react';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { isEmpty } from 'lodash';

import FreePlanView from './FreePlanView.jsx';
import PurchasablePlanView from './PurchasablePlanView.jsx';
import PurchasedPlanView from './PurchasedPlanView.jsx';
import IndividualPlanOrgUpsellView from './IndividualPlanOrgUpsellView.jsx';

import Heading from 'components/Heading.jsx';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';

import LoadingIndicator from 'components/LoadingIndicator.jsx';
import ErrorReporter from 'app/components/ErrorReporter.jsx';

type Props = {
  data: ReactApolloData<IndividualPlanViewQuery>,
};

class IndividualPlanContainer extends React.Component<Props> {
  render() {
    const { data } = this.props;

    if (data.loading) {
      return <LoadingIndicator />;
    }

    if (data.error) {
      return <ErrorReporter error={data.error} />;
    }

    if (!data.me) {
      return <ErrorReporter error="Can't find plan for current user." />;
    }

    const { individualPlans, organizations } = data.me;

    const freeIndividualPlan = individualPlans.find(
      plan => plan.__typename === 'FreeIndividualPlan'
    );
    const purchasedIndividualPlan = individualPlans.find(
      plan => plan.__typename === 'PurchasedIndividualPlan'
    );
    const purchasableIndividualPlan = individualPlans.find(
      plan => plan.__typename === 'PurchasableIndividualPlan'
    );

    if (freeIndividualPlan && freeIndividualPlan.__typename !== 'FreeIndividualPlan') {
      return <ErrorReporter error="Error Loading FreePlan" />;
    }

    const hasFreePlan = isEmpty(purchasedIndividualPlan);

    const inPaidOrg = organizations.some(org => org && org.paid);

    const upSellOrg = organizations.find(org => org && !org.paid);

    const upSellOrgUUID = upSellOrg ? upSellOrg.uuid : undefined;

    if (!organizations) {
      return <ErrorReporter error="Error Loading User Organization Data" />;
    }

    return (
      <div>
        <Row>
          <Heading level="2">Remind Plans</Heading>
          <Text color="eclipse" size="medium">
            Get access to premium features to improve your Remind experience
          </Text>
        </Row>
        <Row>
          <FreePlanView hasFreePlan={hasFreePlan} freeIndividualPlan={freeIndividualPlan} />
        </Row>
        <Row>
          {purchasableIndividualPlan && (
            <PurchasablePlanView inPaidOrg={inPaidOrg} plan={purchasableIndividualPlan} />
          )}
          {purchasedIndividualPlan && (
            <PurchasedPlanView inPaidOrg={inPaidOrg} plan={purchasedIndividualPlan} />
          )}
        </Row>
        <Row>
          {organizations.length > 0 && (
            <IndividualPlanOrgUpsellView inPaidOrg={inPaidOrg} upSellOrgUUID={upSellOrgUUID} />
          )}
        </Row>
      </div>
    );
  }
}

const withData = graphql(
  gql`
    query IndividualPlanView {
      me {
        id
        uuid
        organizations {
          uuid
          paid
        }
        individualPlans(includeFreePlan: true) {
          productTitle
          productDetails {
            text
            learnMoreLink
          }

          ... on FreeIndividualPlan {
            activeSince
          }

          ... on PurchasedIndividualPlan {
            purchasedAt
            activeUntil
            isCancelled
            billingInfoText
          }

          ... on PurchasableIndividualPlan {
            purchaseOptions {
              monthly {
                priceText
              }
              yearly {
                priceText
              }
            }
          }
        }
      }
    }
  `
);

export { IndividualPlanContainer };
export default withData(IndividualPlanContainer);
